/**
 * Environment variable names used across Prisco.
 *
 * This package is imported by BOTH the Node API and the browser app, therefore it
 * must stay free of secrets and free of any Node.js / browser specific API.
 * Only names, public defaults and types live here.
 */
export const ENV_KEYS = {
    /** Application identity */
    APP: {
        NODE_ENV: 'NODE_ENV',
        APP_VERSION: 'APP_VERSION',
        APP_NAME: 'APP_NAME',
    },
    /** NestJS API */
    API: {
        API_PORT: 'API_PORT',
        API_HOST: 'API_HOST',
        API_GLOBAL_PREFIX: 'API_GLOBAL_PREFIX',
        API_CORS_ORIGINS: 'API_CORS_ORIGINS',
        API_RATE_LIMIT_WINDOW_MS: 'API_RATE_LIMIT_WINDOW_MS',
        API_RATE_LIMIT_MAX: 'API_RATE_LIMIT_MAX',
        API_BODY_LIMIT: 'API_BODY_LIMIT',
        /** Where uploaded project logos are stored (ADR-34). Absolute; required in production. */
        UPLOADS_DIR: 'UPLOADS_DIR',
        /**
         * Comma-separated peers whose `X-Forwarded-For` is believed: IPs, CIDRs,
         * `loopback`, `linklocal`, `uniquelocal`, or `none`. Default `loopback`
         * (Nginx on the same host). Never a hop count.
         */
        TRUSTED_PROXIES: 'TRUSTED_PROXIES',
    },
    /** Database (Sequelize / PostgreSQL) */
    DATABASE: {
        DATABASE_URL: 'DATABASE_URL',
        DATABASE_POOL_SIZE: 'DATABASE_POOL_SIZE',
        DATABASE_SSL: 'DATABASE_SSL',
    },
    /** Redis */
    REDIS: {
        REDIS_URL: 'REDIS_URL',
        REDIS_PREFIX: 'REDIS_PREFIX',
    },
    /** Authentication — values are server-side secrets, never exposed to the browser */
    AUTH: {
        JWT_ACCESS_SECRET: 'JWT_ACCESS_SECRET',
        JWT_ACCESS_TTL_SECONDS: 'JWT_ACCESS_TTL_SECONDS',
        JWT_REFRESH_SECRET: 'JWT_REFRESH_SECRET',
        JWT_REFRESH_TTL_SECONDS: 'JWT_REFRESH_TTL_SECONDS',
        JWT_ISSUER: 'JWT_ISSUER',
        JWT_AUDIENCE: 'JWT_AUDIENCE',
        AUTH_REFRESH_COOKIE_SECURE: 'AUTH_REFRESH_COOKIE_SECURE',
        /** Microsoft sign-in attempts allowed per client IP per window. */
        AUTH_LOGIN_RATE_LIMIT_MAX: 'AUTH_LOGIN_RATE_LIMIT_MAX',
        AUTH_LOGIN_RATE_LIMIT_WINDOW_MS: 'AUTH_LOGIN_RATE_LIMIT_WINDOW_MS',
        /** Admin routes that send an email, per client IP per window. */
        AUTH_TOKEN_RATE_LIMIT_MAX: 'AUTH_TOKEN_RATE_LIMIT_MAX',
        AUTH_TOKEN_RATE_LIMIT_WINDOW_MS: 'AUTH_TOKEN_RATE_LIMIT_WINDOW_MS',
        /** Public origin of the SPA, where the invitation email sends people to sign in. */
        APP_PUBLIC_URL: 'APP_PUBLIC_URL',
        /**
         * DEV-1: `true` mounts `POST /auth/dev-login`, and only when `NODE_ENV` is
         * explicitly `development`. The API refuses to boot with it set in production.
         */
        AUTH_DEV_LOGIN_ENABLED: 'AUTH_DEV_LOGIN_ENABLED',
    },
    /**
     * Microsoft Entra ID sign-in (ADR-28). None of these is a secret: the API holds
     * no client secret and only reads Microsoft's public signing keys.
     */
    MICROSOFT: {
        /** The Entra application (client) id. ID tokens must carry it as `aud`. */
        MICROSOFT_CLIENT_ID: 'MICROSOFT_CLIENT_ID',
        /** `common`, `organizations`, or one tenant id. */
        MICROSOFT_TENANT: 'MICROSOFT_TENANT',
        /** Comma-separated tenant ids whose tokens are accepted. Required for `common`. */
        MICROSOFT_TENANT_ALLOWED_TENANT_IDS: 'MICROSOFT_TENANT_ALLOWED_TENANT_IDS',
        /** Allowed clock skew for `exp`/`nbf`/`iat`, 0–300 seconds. */
        MICROSOFT_CLOCK_TOLERANCE_SECONDS: 'MICROSOFT_CLOCK_TOLERANCE_SECONDS',
    },
    /**
     * Outgoing email (the informational invitation). `SMTP_PASSWORD` is a
     * server-side secret; none of these are ever exposed to the browser.
     */
    EMAIL: {
        /** `smtp` (required in production) or `outbox` (development: write .eml files). */
        EMAIL_TRANSPORT: 'EMAIL_TRANSPORT',
        EMAIL_OUTBOX_DIR: 'EMAIL_OUTBOX_DIR',
        /** Who recipients should contact, shown in the email body. */
        EMAIL_SUPPORT_CONTACT: 'EMAIL_SUPPORT_CONTACT',
        SMTP_HOST: 'SMTP_HOST',
        SMTP_PORT: 'SMTP_PORT',
        /** Implicit TLS (usually port 465). Defaults to true on port 465. */
        SMTP_SECURE: 'SMTP_SECURE',
        /** Require STARTTLS when not using implicit TLS. Always on in production. */
        SMTP_REQUIRE_TLS: 'SMTP_REQUIRE_TLS',
        SMTP_USER: 'SMTP_USER',
        SMTP_PASSWORD: 'SMTP_PASSWORD',
        SMTP_FROM: 'SMTP_FROM',
    },
    /** The secure gateway for confidential projects (ADR-40). None is a secret. */
    GATEWAY: {
        /**
         * Comma-separated origins the gateway may proxy to, as reachable from the
         * host the API runs on (`scheme://host:port`). Empty: none (SSRF allowlist).
         */
        GATEWAY_ALLOWED_UPSTREAMS: 'GATEWAY_ALLOWED_UPSTREAMS',
        /**
         * Comma-separated `name=/absolute/dir` pairs: built static web apps on the
         * API's own host that the gateway may serve. A project names a root; the
         * directory never leaves this variable. Empty: none.
         */
        GATEWAY_STATIC_ROOTS: 'GATEWAY_STATIC_ROOTS',
        /** Lifetime of the gateway session cookie, capped by the Prisco session. */
        GATEWAY_SESSION_TTL_SECONDS: 'GATEWAY_SESSION_TTL_SECONDS',
        /** How long to wait for an upstream connection before answering `502`. */
        GATEWAY_UPSTREAM_CONNECT_TIMEOUT_MS: 'GATEWAY_UPSTREAM_CONNECT_TIMEOUT_MS',
        /**
         * Activity tracking inside confidential projects (ADR-41): the tracker
         * script, its event endpoint, and data-change records. Default `true`;
         * `false` turns all three off without touching project opens or page loads.
         */
        GATEWAY_ACTIVITY_TRACKING: 'GATEWAY_ACTIVITY_TRACKING',
        /**
         * Extra CSP sources for named confidential projects, appended to the
         * gateway's `connect-src` / `frame-src` for that project only:
         * `<project-uuid> connect-src=<origin>,<origin> frame-src=<origin>; …`.
         * Bare `https://` or `wss://` origins, never ProjectHub's own host.
         * Empty: the gateway CSP is unchanged.
         */
        GATEWAY_CSP_EXTRA: 'GATEWAY_CSP_EXTRA',
    },
    /** Estimated active time and public URL tracking (ADR-45, ADR-47). None is a secret. */
    ACTIVITY: {
        /**
         * A visible page with no interaction for this many seconds stops counting
         * as active, in confidential (gateway) and public projects alike.
         * Default 120; 30 to 1800.
         */
        ACTIVE_TIME_IDLE_SECONDS: 'ACTIVE_TIME_IDLE_SECONDS',
        /**
         * Public URL tracking (ADR-47): the public tracker script and its event
         * endpoint. Default `false`: both answer `404` and nothing is stored.
         */
        PUBLIC_ACTIVITY_TRACKING: 'PUBLIC_ACTIVITY_TRACKING',
        /** Days raw public tracking events are kept. Default 90. */
        PUBLIC_ACTIVITY_EVENT_RETENTION_DAYS: 'PUBLIC_ACTIVITY_EVENT_RETENTION_DAYS',
        /** Days public visit sessions (the analytics) are kept. Default 400; at least the event retention. */
        PUBLIC_ACTIVITY_SESSION_RETENTION_DAYS: 'PUBLIC_ACTIVITY_SESSION_RETENTION_DAYS',
    },
    /** IP geolocation of access-log events (ADR-42). None is a secret. */
    GEOIP: {
        /**
         * Absolute path to a GeoIP2-format City database (`.mmdb`), e.g. DB-IP City
         * Lite. Never committed. Empty: locations are not resolved, and logging
         * carries on without them.
         */
        GEOIP_DATABASE_PATH: 'GEOIP_DATABASE_PATH',
        /**
         * Absolute directory holding GeoNames `cities15000.txt` and
         * `admin1CodesASCII.txt` (CC BY 4.0), used to turn a device location the
         * user chose to share into a city (ADR-43). Empty: device locations are not
         * resolved, and the IP estimate is used.
         */
        GEONAMES_DATA_DIR: 'GEONAMES_DATA_DIR',
    },
    /**
     * Read only by the Super Admin bootstrap CLI, never by the API process (ADR-20,
     * ADR-32). There is no password: the account binds on its first Microsoft sign-in.
     */
    BOOTSTRAP: {
        INITIAL_ADMIN_EMAIL: 'INITIAL_ADMIN_EMAIL',
        INITIAL_ADMIN_FIRST_NAME: 'INITIAL_ADMIN_FIRST_NAME',
        INITIAL_ADMIN_LAST_NAME: 'INITIAL_ADMIN_LAST_NAME',
        /** The tenant the operator expects; echoed in the output, never stored. */
        INITIAL_ADMIN_MICROSOFT_TENANT_ID: 'INITIAL_ADMIN_MICROSOFT_TENANT_ID',
        ALLOW_SUPER_ADMIN_BOOTSTRAP: 'ALLOW_SUPER_ADMIN_BOOTSTRAP',
    },
    /** Web app (public, Vite `VITE_` prefixed) */
    WEB: {
        VITE_APP_NAME: 'VITE_APP_NAME',
        VITE_API_BASE_URL: 'VITE_API_BASE_URL',
        VITE_APP_ENV: 'VITE_APP_ENV',
    },
};
/** Environment names the project recognises. */
export const APP_ENVIRONMENTS = ['development', 'test', 'production'];
/** Runtime environments the Vite web app may be built for. */
export const APP_MODES = ['development', 'production', 'test'];
/** Public, non-secret defaults. Secrets intentionally have no default. */
export const APP_DEFAULTS = {
    appName: 'Prisco',
    organisation: 'Prakash Software Solutions',
    apiPort: 4000,
    apiHost: '0.0.0.0',
    apiGlobalPrefix: 'api',
    webPort: 5173,
    rateLimitWindowMs: 60_000,
    rateLimitMax: 120,
    loginRateLimitWindowMs: 60_000,
    loginRateLimitMax: 10,
    tokenRateLimitWindowMs: 60_000,
    tokenRateLimitMax: 5,
    appPublicUrl: 'http://localhost:5173',
    microsoftTenant: 'common',
    microsoftClockToleranceSeconds: 60,
    emailOutboxDir: '.mail-outbox',
    emailSupportContact: 'your Prisco administrator',
    smtpPort: 587,
    bodyLimit: '256kb',
    uploadsDir: '.uploads',
    redisPrefix: 'psspl-prisco',
    accessTokenTtlSeconds: 900,
    refreshTokenTtlSeconds: 604_800,
    gatewaySessionTtlSeconds: 28_800,
    gatewayUpstreamConnectTimeoutMs: 10_000,
    activeTimeIdleSeconds: 120,
    publicActivityEventRetentionDays: 90,
    publicActivitySessionRetentionDays: 400,
};
/** Stable identifier used for JWT `iss` / `aud` claims. */
export const APP_IDENTIFIERS = {
    service: 'psspl-prisco',
    apiAudience: 'psspl-prisco-api',
    webAudience: 'psspl-prisco-web',
};
//# sourceMappingURL=env.js.map